import { lazy, useEffect, useMemo, useState, Suspense } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { useQuery, useReactiveVar } from '@apollo/client';
import queries from '@nc-aftermarket/config/queryDefs';
import { getCartItems } from '../lib/cartItemsVar';
import getAuthRedirectUrl from '../lib/getAuthRedirectUrl';
import getSalePresenter from '../lib/getSalePresenter';
import isAuthError from '../lib/isAuthError';
import isLoggedInVar from '../lib/isLoggedInVar';
import useWatchMutation from '../lib/useWatchMutation';
import Auctions from '../pages/Auctions';
import BuyNow from '../pages/BuyNow';
import Collection from '../pages/Collection';
import Home from '../pages/Home';
import Sale from '../pages/Sale';
import AppDialog from './AppDialog';
import AppStatus from './AppStatus';
import Loader from './Loader';
import Nav from './Nav';

const Dashboard = lazy(() => import('../pages/Dashboard'));
const Login = lazy(() => import('../pages/Login'));
const Logout = lazy(() => import('../pages/Logout'));
const Subscribe = lazy(() => import('../pages/Subscribe'));

export default function App() {
  const history = useHistory();
  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const [watch, watchQuery] = useWatchMutation();
  const isLoggedIn = useReactiveVar(isLoggedInVar);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const userContextQuery = useQuery(queries.UserContext, {
    fetchPolicy: 'no-cache',
  });

  // Handle watch auth redirects
  useEffect(() => {
    if (params.get('watch') && !watchQuery.loading) {
      watch({
        add: params.get('watch').split(','),
      })
        .then(() => {
          params.delete('watch');
          history.replace({
            ...location,
            search: `?${params.toString()}`,
          });
        })
        .catch(() => {
          window.alert(`Failed to watch domain.`);
        });
    }
  }, [params, history, location, watch, watchQuery.loading]);

  // Initialize isLoggedInVar
  useEffect(() => {
    if (
      userContextQuery.loading ||
      (userContextQuery.error && !isAuthError(userContextQuery.error))
    ) {
      return;
    }
    if (userContextQuery.data?.self) {
      isLoggedInVar(true);
    } else {
      isLoggedInVar(false);
    }
  }, [userContextQuery]);

  // Disabling until this query can be optimized:
  useEffect(() => {
    if (isLoggedIn === true) {
      userContextQuery.startPolling(15 * 1000);
    } else if (isLoggedIn === false) {
      userContextQuery.stopPolling();
    }
  }, [isLoggedIn, userContextQuery]);

  // Initialize cartItemsVar
  useEffect(() => {
    getCartItems();
  }, []);

  useEffect(() => {
    if (!userContextQuery.previousData) return;
    const outbidSale = userContextQuery.data?.self?.bidding?.items?.find(
      sale => {
        const previousSaleData =
          userContextQuery.previousData.self?.bidding?.items?.find(
            prev => prev.id === sale.id,
          );
        return (
          previousSaleData?.selfIsLeadingBidder === true &&
          sale.selfIsLeadingBidder === false &&
          sale.status === 'active' &&
          sale.bids[1]?.user.isSelf &&
          sale.bids[1].status === 'processed'
        );
      },
    );
    if (outbidSale) {
      document.dispatchEvent(
        new CustomEvent(AppDialog.events.open, {
          detail: {
            key: 'outbid',
            sale: getSalePresenter(outbidSale),
          },
        }),
      );
    }
  }, [userContextQuery.data, userContextQuery.previousData]);

  return (
    <>
      <Nav />
      <AppStatus />
      <AppDialog />
      {isRedirecting ? (
        <Loader />
      ) : (
        <Suspense fallback={<Loader />}>
          <Switch>
            <Redirect from="/collection/:slug" to="/:slug" />
            {/* Force trailing slash to be consistent with Namecheap.com URL schema */}
            <Route
              path="/:url*"
              exact
              strict
              render={props => {
                console.log(
                  'Redirect to',
                  `${props.location.pathname}/${props.location.search}`,
                );
                return (
                  <Redirect
                    to={`${props.location.pathname}/${props.location.search}`}
                  />
                );
              }}
            />
            <Route component={Auctions} path="/auctions" />
            <Route component={Sale} path="/buynow/:bindomain" />
            <Route component={BuyNow} path="/buynow" />
            <Route
              path="/dashboard"
              render={({ location }) => {
                // We don't know yet if the user is logged in
                if (isLoggedIn === null) {
                  return null;
                }
                if (isLoggedIn) {
                  return <Dashboard />;
                } else {
                  // Prevent trapping the user in a login redirect loop if they navigate back from the SSO login screen.
                  setIsRedirecting(true);
                  history.replace('/');
                  window.location = getAuthRedirectUrl(location.pathname);
                  return null;
                }
              }}
            />
            <Route component={Login} path="/login" />
            <Route component={Logout} path="/logout" />
            <Route component={Sale} path="/sale/:id/:name?" />
            <Route component={Subscribe} path="/subscribe" />
            <Route
              path="/:slug"
              render={props => {
                if (/\w+(\.\w+)+/.test(props.match.params.slug)) {
                  return <Sale />;
                }
                return <Collection />;
              }}
            />
            <Route component={Home} path="/" />
          </Switch>
        </Suspense>
      )}
    </>
  );
}
